import { assertToBeDefine } from '@core/core/utils'

import { isArrayOfIds, isFilterForm, isFilterFormWithVsp } from '../../types'
import type { Step, Steps, StepsContext } from './types'

type StepControllerState = {
  stepPath: Step[]
  activeStep: Step
}

export class StepController {
  private state: StepControllerState

  private subscriptions = new Set<VoidFunction>()

  public constructor() {
    const initialStep: Step = { label: 'СЗ', id: 'chooseBaseWell' }
    this.state = {
      stepPath: [initialStep],
      activeStep: initialStep,
    }
  }

  public setContextToActiveStep<T extends Steps>(step: T, context?: StepsContext[T]) {
    const activeStep = this.getStep(-1)

    if (step !== activeStep.id) {
      throw new Error(
        `Active step was ${activeStep.id}, but trying to set context to the step ${step}`,
      )
    }

    activeStep.context = context

    this.state.stepPath.pop()
    this.state.stepPath.push(activeStep)

    this.notify()
  }

  public goToStep(step: Steps) {
    const indexToGo = this.state.stepPath.findIndex(({ id }) => id === step)
    this.state.stepPath.splice(indexToGo + 1)

    this.notify()
  }

  public addNextStep<T extends Steps>(label: string, step: T, context?: StepsContext[T]) {
    const stepElement = this.createElementWithContext(label, step, context)

    this.state.stepPath.push(stepElement)

    this.notify()
  }

  public getStep(identifier: number | Steps) {
    const step =
      typeof identifier === 'number'
        ? this.state.stepPath.at(identifier)
        : this.state.stepPath.find(step => step.id === identifier)

    assertToBeDefine(step)

    return step
  }

  private notify() {
    this.state.activeStep = this.getStep(-1)
    this.state = structuredClone(this.state)

    this.subscriptions.forEach(callback => {
      callback()
    })
  }

  public subscribe(cb: VoidFunction) {
    this.subscriptions.add(cb)

    return () => {
      this.subscriptions.delete(cb)
    }
  }

  public getSnapshot() {
    return this.state
  }

  private createElementWithContext<T extends Steps>(
    label: string,
    step: T,
    context?: StepsContext[T],
  ) {
    const stepElement: Step = { id: step, label }

    switch (stepElement.id) {
      case 'chooseTargetWells':
        if (isFilterFormWithVsp(context)) {
          stepElement.context = context
        }
        break
      case 'chooseBaseWell':
      case 'chooseWellWithVsp':
        if (isFilterForm(context)) {
          stepElement.context = context
        }
        break

      case 'wellVspSettings':
      case 'chooseVsp':
        if (typeof context === 'string') {
          stepElement.context = context
        }
        break
      case 'applyVsp':
        if (isArrayOfIds(context)) {
          stepElement.context = context
        }
        break
    }
    return stepElement
  }
}
